import React, { memo, useState } from 'react';
import { Grid, CircularProgress } from '@mui/material';

import Header from 'modules/views/shared/Header';
import Pagination from 'modules/views/shared/Pagination';
import { useAppSelector } from 'modules/state-mgmt/store';

import {
  AppointmentCard,
  BookAppointmentModal,
  DeleteAppointmentModal,
  DeleteTestResultModal,
  DrugTestingHeader,
  DrugTestingTabs,
  EmptyState,
  TestResultCard,
  UploadResultsModal,
} from './components';
import { useDrugTestingList } from './hooks/useDrugTestingList';
import { IDrugTestAppointment, IThirdPartyTestResult, ITestResultAttachment, TabIndex } from './types';
import { useStyles } from './styles';

const MyDrugTesting: React.FC = () => {
  const classes = useStyles();
  const { isWorker } = useAppSelector(state => ({
    isWorker: state.user?.isWorker,
  }));

  const {
    appointments,
    testResults,
    counts,
    activeTab,
    page,
    paginationInfo,
    isLoading,
    handleTabChange,
    handlePageChange,
    refetch,
    refetchThirdPartyResults,
  } = useDrugTestingList();

  const [isBookAppointmentOpen, setIsBookAppointmentOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<IDrugTestAppointment | null>(null);
  const [isUploadResultsOpen, setIsUploadResultsOpen] = useState(false);
  const [editingTestResult, setEditingTestResult] = useState<IThirdPartyTestResult | null>(null);
  const [deletingTestResult, setDeletingTestResult] = useState<IThirdPartyTestResult | null>(null);
  const [deletingAppointment, setDeletingAppointment] = useState<IDrugTestAppointment | null>(null);

  const handleBookAppointment = () => {
    setEditingAppointment(null);
    setIsBookAppointmentOpen(true);
  };
  const handleCloseBookAppointment = () => {
    setIsBookAppointmentOpen(false);
    setEditingAppointment(null);
  };
  const handleConfirmBookAppointment = () => {
    setIsBookAppointmentOpen(false);
    setEditingAppointment(null);
    refetch();
  };

  const handleUploadResults = () => {
    setEditingTestResult(null);
    setIsUploadResultsOpen(true);
  };
  const handleCloseUploadResults = () => {
    setIsUploadResultsOpen(false);
    setEditingTestResult(null);
  };
  const handleConfirmUploadResults = () => {
    setIsUploadResultsOpen(false);
    setEditingTestResult(null);
    refetchThirdPartyResults();
  };

  const handleEditAppointment = (appointment: IDrugTestAppointment) => {
    setEditingAppointment(appointment);
    setIsBookAppointmentOpen(true);
  };

  const handleEditTestResult = (testResult: IThirdPartyTestResult) => {
    setEditingTestResult(testResult);
    setIsUploadResultsOpen(true);
  };

  const handleDeleteTestResult = (testResult: IThirdPartyTestResult) => {
    setDeletingTestResult(testResult);
  };
  const handleCloseDeleteTestResult = () => setDeletingTestResult(null);
  const handleDeleteTestResultSuccess = () => {
    refetchThirdPartyResults();
  };

  const handleDeleteAppointment = (appointment: IDrugTestAppointment) => {
    setDeletingAppointment(appointment);
  };
  const handleCloseDeleteAppointment = () => setDeletingAppointment(null);
  const handleDeleteAppointmentSuccess = () => {
    refetch();
  };

  const handleViewMoreAppointment = (_appointment: IDrugTestAppointment) => {
    // Placeholder — wired up in a follow-up
  };

  const handleViewMoreTestResult = (_testResult: IThirdPartyTestResult) => {
    // Placeholder — wired up in a follow-up
  };

  const handleOpenAttachment = (attachment: ITestResultAttachment) => {
    if (attachment.url) window.open(attachment.url, '_blank', 'noopener,noreferrer');
  };

  const onPageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    handlePageChange(value);
  };

  const renderContent = () => {
    if (activeTab === TabIndex.THIRD_PARTY_RESULTS) {
      if (testResults.length === 0) {
        return <EmptyState activeTab={activeTab} />;
      }

      return (
        <Grid container spacing={3} className={classes.appointmentsGrid} data-testid="my-drug-testing-grid">
          {testResults.map(testResult => (
            <Grid item xs={12} md={6} key={testResult.id}>
              <TestResultCard
                testResult={testResult}
                onEdit={handleEditTestResult}
                onDelete={handleDeleteTestResult}
                onViewMore={handleViewMoreTestResult}
                onOpenAttachment={handleOpenAttachment}
              />
            </Grid>
          ))}
        </Grid>
      );
    }

    if (appointments.length === 0) {
      return <EmptyState activeTab={activeTab} />;
    }

    return (
      <Grid container spacing={3} className={classes.appointmentsGrid} data-testid="my-drug-testing-grid">
        {appointments.map(appointment => (
          <Grid item xs={12} md={6} key={appointment.id}>
            <AppointmentCard
              appointment={appointment}
              onEdit={handleEditAppointment}
              onDelete={handleDeleteAppointment}
              onViewMore={handleViewMoreAppointment}
            />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <div className={`${classes.container} ${!isWorker ? classes.containerWithWarning : ''}`}>
      <Header styleClass={classes.mainHeader} showOnlineWarning={!isWorker} />

      <DrugTestingHeader />

      <DrugTestingTabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        counts={counts}
        onBookAppointment={handleBookAppointment}
        onUploadResults={handleUploadResults}
      />

      {isLoading && (
        <div
          style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}
          data-testid="my-drug-testing-loader"
        >
          <CircularProgress />
        </div>
      )}

      <div className={classes.content} data-testid="my-drug-testing-content">
        {!isLoading && renderContent()}
      </div>

      {!isLoading && paginationInfo.totalResults > 0 && (
        <div className={classes.paginationWrapper}>
          <Pagination
            count={Math.ceil(paginationInfo.totalResults / paginationInfo.pageSize)}
            page={page}
            onChange={onPageChange}
            siblingCount={1}
            boundaryCount={3}
            testId="my-drug-testing-pagination"
          />
        </div>
      )}

      <BookAppointmentModal
        isVisible={isBookAppointmentOpen}
        onClose={handleCloseBookAppointment}
        onConfirm={handleConfirmBookAppointment}
        appointment={editingAppointment}
      />

      <UploadResultsModal
        isVisible={isUploadResultsOpen}
        onClose={handleCloseUploadResults}
        onConfirm={handleConfirmUploadResults}
        testResult={editingTestResult}
      />

      <DeleteTestResultModal
        isVisible={!!deletingTestResult?.id}
        testResult={deletingTestResult}
        onClose={handleCloseDeleteTestResult}
        onDeleteSuccess={handleDeleteTestResultSuccess}
      />

      <DeleteAppointmentModal
        isVisible={!!deletingAppointment?.id}
        appointment={deletingAppointment}
        onClose={handleCloseDeleteAppointment}
        onDeleteSuccess={handleDeleteAppointmentSuccess}
      />
    </div>
  );
};

export default memo(MyDrugTesting);
